import { useEffect, useState } from 'react';

import { isDate, isValid, parseISO } from 'date-fns';
import { get } from 'lodash';

const useSort = (data: any[], initialSortType = 'name', initialSortOrder: 'asc' | 'desc' = 'asc') => {
  const [sortType, setSortType] = useState(initialSortType);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
  const [sortedData, setSortedData] = useState<any>([]);
  const priorities = ['high', 'medium', 'low'];

  useEffect(() => {
    if (!data) return;

    const sort = (a, b) => {
      if (sortType === 'priority') return priorities.indexOf(get(a, sortType)) - priorities.indexOf(get(b, sortType));

      const valA = get(a, sortType);
      const valB = get(b, sortType);

      if (valA === null || valA === undefined) return 1;
      if (valB === null || valB === undefined) return -1;

      // 1. Explicit Number Comparison
      if (typeof valA === 'number' && typeof valB === 'number') return valA - valB;

      // 2. Date Comparison (Actual Date objects)
      if (isDate(valA) && isDate(valB)) return valA.getTime() - valB.getTime();

      // 3. String that looks like a number
      if (typeof valA === 'string' && typeof valB === 'string') {
        const numA = parseFloat(valA);
        const numB = parseFloat(valB);
        // Only treat as number if it's a "clean" number string (not a date string starting with numbers)
        if (!Number.isNaN(numA) && !Number.isNaN(numB) && String(numA) === valA && String(numB) === valB) return numA - numB;

        // 4. ISO Date strings
        if (valA.length > 8 && valB.length > 8) {
          const dateA = parseISO(valA);
          const dateB = parseISO(valB);
          if (isValid(dateA) && isValid(dateB)) return dateA.getTime() - dateB.getTime();
        }
      }

      // 5. String comparison (fallback)
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();

      return strA.localeCompare(strB);
    };

    const sorted = [...data].sort((a, b) => {
      const result = sort(a, b);
      return sortOrder === 'asc' ? result : -result;
    });

    setSortedData(sorted);
  }, [JSON.stringify(data), sortType, sortOrder]);

  return {
    setSortOrder,
    setSortType,
    sortedData,
    sortOrder,
    sortType,
  };
};

export default useSort;
